import React, { ChangeEvent, useEffect, useState } from "react";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";
import { Camera, CameraResultType } from "@capacitor/camera";
import classNames from "classnames";
import Icon from "@mdi/react";
import { mdiCameraOutline, mdiDeleteOutline } from "@mdi/js";
import { Box, Button, ButtonBase, CircularProgress, Dialog, IconButton, Typography } from "@mui/material";
import { Capacitor } from "@capacitor/core";

import useNotifier from "hooks/useNotifier";
import b64toBlob from "utils/b64toBlob";
import AppBar from "../AppBar";
import ContrastIcon from "../ContrastIcon";
import CropImage from "../CropImage";
import SlideUpTransition from "../SlideUpTransition";
import styles from "./avatarPicker.module.scss";

export interface AvatarPickerProps extends Omit<React.HTMLProps<HTMLDivElement>, "placeholder"> {
  id: string;
  img?: string;
  placeholder: React.ReactNode;
  onDeleteImage: () => Promise<void> | void;
  onSelectImage: (image: Blob) => Promise<void> | void;
}

const ALLOWED_FILE_TYPES = ["jpeg", "jpg", "png", "bpm", "gif", "webp"];

function AvatarPicker(props: AvatarPickerProps) {
  const { id, img, placeholder, onDeleteImage, onSelectImage, ...rest } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [dialogType, setDialogType] = useState<"profile" | "crop" | null>(null);
  const [imgSrc, setImgSrc] = useState<string | ArrayBuffer | null>(null);
  const { showNotification } = useNotifier();
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();

  /**
   * Open image which will be used to as avatar. This will be basses to the CropImage
   * component
   *
   * @param event
   */
  function handleGetWebPhoto(event: ChangeEvent<HTMLInputElement>): void {
    if (!event.target?.files?.length) {
      return;
    }

    const file = event.target.files[0];
    const fileType = file.type.split("/").pop()!.toLowerCase();

    if (!ALLOWED_FILE_TYPES.includes(fileType)) {
      showNotification({
        message: "Selected file is not supported.",
        severity: "error",
      });
      return;
    }

    setDialogType("crop");
    navigate(location.pathname);

    const reader = new FileReader();
    reader.addEventListener("load", () => setImgSrc(reader.result));
    reader.readAsDataURL(event.target.files[0]);
  }

  async function handleGetNativePhoto() {
    const image = await Camera.getPhoto({
      resultType: CameraResultType.Base64,
    });

    if (!image.base64String) {
      return;
    }

    if (!ALLOWED_FILE_TYPES.includes(image.format)) {
      showNotification({
        message: "Selected file is not supported.",
        severity: "error",
      });
      return;
    }

    setDialogType("crop");
    navigate(location.pathname);

    const reader = new FileReader();
    reader.addEventListener("load", () => setImgSrc(reader.result));
    reader.readAsDataURL(b64toBlob(image.base64String));
  }

  /**
   * Handle crop image cancel
   */
  function handleCropImageCancel() {
    setDialogType(null);
    navigate(-1);
  }

  /**
   * Handle crop image done
   *
   * @param image
   */
  async function handleCropImageDone(image: Blob | null) {
    setDialogType(null);
    navigate(-1);

    if (image) {
      setIsLoading(true);
      await onSelectImage(image);
    }

    setIsLoading(false);
  }

  /**
   * Handle profile image click
   */
  function handleProfileImageClick() {
    if (img) {
      setDialogType("profile");
      navigate(location.pathname);
    }
  }

  /**
   * Handle delete image
   */
  async function handleDeleteImage() {
    setDialogType(null);
    navigate(-1);

    setIsLoading(true);
    await onDeleteImage();
    setIsLoading(false);
  }

  /**
   * On pressing back button close opened dialogs
   */
  useEffect(() => {
    if (navigationType === "POP") {
      setDialogType(null);
    }
  }, [navigationType]);

  function renderCropImageDialog() {
    return (
      <Dialog fullScreen open={!!dialogType} TransitionComponent={SlideUpTransition} className={styles.dialog}>
        <AppBar
          actions={
            dialogType === "profile"
              ? [
                  <IconButton key='action-1' onClick={handleDeleteImage}>
                    <ContrastIcon path={mdiDeleteOutline} title='Delete image' size={1} />
                  </IconButton>,
                ]
              : undefined
          }
          backAction={dialogType === "profile"}
          noRadius
          title={dialogType === "crop" ? "Crop image" : "Avatar image"}
        />
        {!dialogType || dialogType === "profile" ? (
          <div className={styles.avatarInnerContainer}>
            <img src={img} alt='Avatar' className={styles.avatarImage} />
          </div>
        ) : (
          <CropImage onCancel={handleCropImageCancel} onDone={handleCropImageDone} imgSrc={imgSrc} />
        )}
      </Dialog>
    );
  }

  function renderUploadImage() {
    return (
      <Box
        sx={(theme) => ({
          bgcolor: theme.palette.mode === "light" ? "#eeeeee" : "rgba(255, 255, 255, 0.05)",
        })}
        className={classNames(styles.avatarInnerContainer)}
      >
        {typeof placeholder === "string" ? <Typography fontSize={42}>{placeholder}</Typography> : placeholder}
      </Box>
    );
  }

  function renderProfileImage() {
    return (
      <Button sx={{ borderRadius: "50%", p: 0 }} onClick={handleProfileImageClick}>
        <img className={styles.selectedImage} src={img} alt='Avatar' />
      </Button>
    );
  }

  function renderTrigger() {
    return Capacitor.isNativePlatform() ? (
      <ButtonBase className={styles.triggerButton} onClick={() => handleGetNativePhoto()}>
        <Icon path={mdiCameraOutline} title='Open image' size={0.8} />
      </ButtonBase>
    ) : (
      <>
        <input className={styles.fileInput} type='file' id={id} onChange={handleGetWebPhoto} disabled={isLoading} />
        <label htmlFor={id} className={styles.triggerButton}>
          <Icon path={mdiCameraOutline} title='Open image' size={0.8} />
        </label>
      </>
    );
  }

  const renderPicker = () => {
    if (isLoading) {
      return (
        <div className={styles.avatarInnerContainer}>
          <CircularProgress size={24} />
        </div>
      );
    }

    return img ? renderProfileImage() : renderUploadImage();
  };

  return (
    <div {...rest}>
      <Box
        borderRadius='50%'
        border='1px dashed rgba(145, 158, 171, 0.4)'
        color='#616161'
        display='inline-block'
        height={112}
        padding={1}
        position='relative'
        width={112}
        sx={{ WebkitTapHighlightColor: "transparent" }}
      >
        {renderPicker()}
        {renderTrigger()}
      </Box>
      {renderCropImageDialog()}
    </div>
  );
}

export default AvatarPicker;
